/* eslint-disable no-undef */
import React, { lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { dataDogInit } from 'helpers/dataDog';
import { CDN_TYPE_BASE, getCDNUrl, LocalConfig } from 'utils';

import {
  AnalyticsManager,
  ApiManager,
  Atlas,
  Bus,
  ExtensionsManager,
  initDebug,
  isNotEmptyObject,
  Logger,
  NotificationManager,
  SettingsManager,
  SiteConfigManager,
  StorageManager,
  TranslationProvider,
} from '@atlas/core';
import { clientProfileRoutes, paymentRoutes, staticRoutes } from '@atlas/routing';

import { BTAG_STORAGE_KEY, CXFROM_STORAGE_KEY } from 'common/constants';
import SignUpLayout from 'components/Layout/SignUpLayout';
import history from 'components/MainRouter/history';
import GlobalMaintenanceContainer from 'components/MaintenanceContainer/GlobalMaintenanceContainer';
import { LayoutLoader } from 'src/components/Layout/LayoutLoader';
import subscribeUserOffline from 'src/subscriptions/subscribeUserOffline';

import 'common/Form/formConfig/formConfig';
import '../subscriptions/dataLayer.bindings';

import packageData from '../../package.json';
import resolveFingerprint from '../fingerprint';
import busEventsMapping from '../subscriptions/bus.bindings';

dataDogInit();
initDebug();

const ToastContainer = lazy(
  () => import(/* webpackChunkName: "Notifications" */ 'common/Toast/ToastContainer'),
);
const Notifications = lazy(
  () => import(/* webpackChunkName: "Notifications" */ 'common/Notifications'),
);

const ShareCss = lazy(() => import('modules/share-css'));

const SuspenseCssComponent = () => (
  <Suspense fallback={<div />}>
    <ShareCss />
  </Suspense>
);

// Updating application default settings using local config
const localLanguage = LocalConfig.get('language');
if (localLanguage) {
  SettingsManager.settings.lang = localLanguage;
}

SiteConfigManager.device = 'mobile';

// Only for development
if (process.env.NODE_ENV === 'development') {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const localCmsSettings = require('./cms.settings.js');
  SiteConfigManager.setConfig(localCmsSettings.default);

  // Loading svg sprites from dyeing
  fetch('/dyeing/build/sprite/sprite.svg')
    .then(res => res.text())
    .then(svg => {
      document.body.insertAdjacentHTML('beforeend', svg);
    });
}

// Only for production
if (process.env.NODE_ENV === 'production') {
  // Applying default client application settings
  if (isNotEmptyObject(window.CMS_CONFIG)) {
    const { navigation = {}, page = {} } = window.CMS_CONFIG;

    SiteConfigManager.setConfig({ ...page, navigation });
  }
}

// Set language
SettingsManager.updateSettings({
  isSimpleLogin: true,
});

// Resolving stored settings
SettingsManager.resolveSettings();

busEventsMapping(Bus, history, {
  // Overriding default registration success process that should be turned off
  // for optimized signup
  [Bus.events.auth.formRegistrationSuccess]: () => {},
});
subscribeUserOffline();

const { host, protocol } = window.location;

ApiManager.setHostname('core', `${protocol}//${host}/api/`);
ApiManager.setHostname('cms', `${protocol}//${host}/api/cms-go/v1/site/`);
ApiManager.setHostname('cms/v2', `${protocol}//${host}/api/cms-go/v2/site/`);
ApiManager.setHostname('cdn', getCDNUrl(CDN_TYPE_BASE));

if (process.env.NODE_ENV === 'development') {
  window.SiteConfigManager = SiteConfigManager;
  window.Bus = Bus;
}

// Initializing api default headers
const defaultHeaders = {
  device: 'mobile',
  version: packageData.version,
};
const projectId = LocalConfig.get('projectId');
// Providing project id if it exist in config
if (projectId) {
  defaultHeaders['x-project-id'] = projectId;
}

// Providing header for local testing
if (process.env.NODE_ENV === 'development') {
  defaultHeaders['x-project-team'] = 'frontend-team';
}

ApiManager.setDefaultHeaders(defaultHeaders);

if (!SettingsManager.settings.theme) {
  Logger.debug(`[settings] setting default theme=${SYS_CONFIG.defaultTheme}`);
  SettingsManager.updateSettings({ theme: SYS_CONFIG.defaultTheme });
}

// Creating atlas extensions manager
window.aem = new ExtensionsManager();

// Initializing analytics
AnalyticsManager.init({
  fingerprint: () =>
    resolveFingerprint().then(components => window.btoa(JSON.stringify(components))),
});

const domNode = document.getElementById('root');
const root = createRoot(domNode);

root.render(
  <>
    <SuspenseCssComponent />
    <Suspense fallback={<div />}>
      <Atlas>
        <TranslationProvider>
          <GlobalMaintenanceContainer>
            <LayoutLoader>
              <SignUpLayout optimized={true} />
              <ToastContainer />
              <Notifications />
            </LayoutLoader>
          </GlobalMaintenanceContainer>
        </TranslationProvider>
      </Atlas>
    </Suspense>
  </>,
);
